import {
  MentorPersonalNote,
  FounderPersonalNote,
  PersonalNote,
} from '../contexts/personal-notes-context';
import { axiosRequest } from './instance';

export enum FilterType {
  OWN_NOTES = 'OWN_NOTES',
  EVENT_NOTES = 'EVENT_NOTES',
  VENTURE_NOTES = 'VENTURE_NOTES',
  MENTOR_NOTES = 'MENTOR_NOTES',
  FOUNDER_NOTES = 'FOUNDER_NOTES',
}

export interface PersonalNotesFilterRequest {
  search?: string;
  filterType?: FilterType;
  pageNumber?: number;
  pageSize?: number;
}

const personalNotesAPI = {
  async getMentorPersonalNotes(): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<MentorPersonalNote[]>({
        url: '/mentors/personal-notes',
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async getMentorPersonalNotesByPages(page: number): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<MentorPersonalNote[]>({
        url: `/mentors/personal-notes/pages/${page}`,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async searchMentorPersonalNotes(keywords: string): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<MentorPersonalNote[]>({
        url: `/mentors/personal-notes/search/${keywords}`,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async getMentorPersonalNote(noteId: string): Promise<PersonalNote | null> {
    try {
      const res = await axiosRequest.get<MentorPersonalNote>({
        url: `/mentors/personal-notes/${noteId}`,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return null;
      }
      throw e;
    }
  },
  async createMentorPersonalNotes(
    note: Omit<PersonalNote, 'id' | 'date'>,
  ): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.post<MentorPersonalNote>({
        url: '/mentors/personal-notes',
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async updateMentorPersonalNotes(note: PersonalNote): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.put<MentorPersonalNote>({
        url: '/mentors/personal-notes',
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async removeMentorPersonalNote(note: PersonalNote): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.delete<MentorPersonalNote>({
        url: `/mentors/personal-notes`,
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async getFounderPersonalNotes(): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<FounderPersonalNote[]>({
        url: '/founders/personal-notes',
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async getFounderPersonalNotesByPages(page: number): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<FounderPersonalNote[]>({
        url: `/founders/personal-notes/pages/${page}`,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async searchFounderPersonalNotes(keywords: string): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<FounderPersonalNote[]>({
        url: `/founders/personal-notes/search/${keywords}`,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async getFounderPersonalNote(noteId: string): Promise<PersonalNote | null> {
    try {
      const res = await axiosRequest.get<FounderPersonalNote>({
        url: `/founders/personal-notes/${noteId}`,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return null;
      }
      throw e;
    }
  },
  async createFounderPersonalNotes(
    note: Omit<PersonalNote, 'id' | 'date'>,
  ): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.post<FounderPersonalNote>({
        url: '/founders/personal-notes',
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async updateFounderPersonalNotes(note: PersonalNote): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.put<FounderPersonalNote>({
        url: '/founders/personal-notes',
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async removeFounderPersonalNote(note: PersonalNote): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.delete<FounderPersonalNote>({
        url: `/founders/personal-notes`,
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async getAdminPersonalNotes(): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<MentorPersonalNote[]>({
        url: '/admin/personal-notes',
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async getAdminPersonalNotesByPages(page: number): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<MentorPersonalNote[]>({
        url: `/admin/personal-notes/pages/${page}`,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async searchAdminPersonalNotes(keywords: string): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.get<MentorPersonalNote[]>({
        url: `/admin/personal-notes/search/${keywords}`,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async getAdminPersonalNote(noteId: string): Promise<PersonalNote | null> {
    try {
      const res = await axiosRequest.get<MentorPersonalNote>({
        url: `/admin/personal-notes/${noteId}`,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return null;
      }
      throw e;
    }
  },
  async createAdminPersonalNotes(
    note: Omit<PersonalNote, 'id' | 'date'>,
  ): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.post<MentorPersonalNote>({
        url: '/admin/personal-notes',
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async updateAdminPersonalNotes(note: PersonalNote): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.put<MentorPersonalNote>({
        url: '/admin/personal-notes',
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async removeAdminPersonalNote(note: PersonalNote): Promise<PersonalNote> {
    try {
      const res = await axiosRequest.delete<MentorPersonalNote>({
        url: `/admin/personal-notes`,
        data: note,
        credentials: true,
      });
      return res.data as PersonalNote;
    } catch (e: any) {
      throw e;
    }
  },
  async filterAdminPersonalNotes(
    filterRequest: PersonalNotesFilterRequest,
  ): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.post<MentorPersonalNote[]>({
        url: '/admin/personal-notes/filter',
        data: filterRequest,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async filterFounderPersonalNotes(
    filterRequest: PersonalNotesFilterRequest,
  ): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.post<FounderPersonalNote[]>({
        url: '/founders/personal-notes/filter',
        data: filterRequest,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
  async filterMentorPersonalNotes(
    filterRequest: PersonalNotesFilterRequest,
  ): Promise<PersonalNote[]> {
    try {
      const res = await axiosRequest.post<MentorPersonalNote[]>({
        url: '/mentors/personal-notes/filter',
        data: filterRequest,
        credentials: true,
      });
      return res.data as PersonalNote[];
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return [];
      } else {
        throw e;
      }
    }
  },
};

// Unified API interface
export interface PersonalNotesAPI {
  getPersonalNotes: () => Promise<PersonalNote[]>;
  getPersonalNotesByPages: (page: number) => Promise<PersonalNote[]>;
  getPersonalNote: (noteId: string) => Promise<PersonalNote | null>;
  updatePersonalNotes: (note: PersonalNote) => Promise<PersonalNote>;
  createPersonalNotes: (
    note: Omit<PersonalNote, 'id' | 'date'>,
  ) => Promise<PersonalNote>;
  removePersonalNote: (note: PersonalNote) => Promise<PersonalNote>;
  searchPersonalNotes: (keywords: string) => Promise<PersonalNote[]>;
  filterPersonalNotes: (
    filterRequest: PersonalNotesFilterRequest,
  ) => Promise<PersonalNote[]>;
}

// Role-based API objects with unified interface
export const mentorPersonalNotesAPI: PersonalNotesAPI = {
  getPersonalNotes: personalNotesAPI.getMentorPersonalNotes,
  getPersonalNotesByPages: personalNotesAPI.getMentorPersonalNotesByPages,
  getPersonalNote: personalNotesAPI.getMentorPersonalNote,
  updatePersonalNotes: personalNotesAPI.updateMentorPersonalNotes,
  createPersonalNotes: personalNotesAPI.createMentorPersonalNotes,
  removePersonalNote: personalNotesAPI.removeMentorPersonalNote,
  searchPersonalNotes: personalNotesAPI.searchMentorPersonalNotes,
  filterPersonalNotes: personalNotesAPI.filterMentorPersonalNotes,
};

export const founderPersonalNotesAPI: PersonalNotesAPI = {
  getPersonalNotes: personalNotesAPI.getFounderPersonalNotes,
  getPersonalNotesByPages: personalNotesAPI.getFounderPersonalNotesByPages,
  getPersonalNote: personalNotesAPI.getFounderPersonalNote,
  updatePersonalNotes: personalNotesAPI.updateFounderPersonalNotes,
  createPersonalNotes: personalNotesAPI.createFounderPersonalNotes,
  removePersonalNote: personalNotesAPI.removeFounderPersonalNote,
  searchPersonalNotes: personalNotesAPI.searchFounderPersonalNotes,
  filterPersonalNotes: personalNotesAPI.filterFounderPersonalNotes,
};

export const adminPersonalNotesAPI: PersonalNotesAPI = {
  getPersonalNotes: personalNotesAPI.getAdminPersonalNotes,
  getPersonalNotesByPages: personalNotesAPI.getAdminPersonalNotesByPages,
  getPersonalNote: personalNotesAPI.getAdminPersonalNote,
  updatePersonalNotes: personalNotesAPI.updateAdminPersonalNotes,
  createPersonalNotes: personalNotesAPI.createAdminPersonalNotes,
  removePersonalNote: personalNotesAPI.removeAdminPersonalNote,
  searchPersonalNotes: personalNotesAPI.searchAdminPersonalNotes,
  filterPersonalNotes: personalNotesAPI.filterAdminPersonalNotes,
};

export function getPersonalNotesAPI(
  role: 'admin' | 'founder' | 'mentor',
): PersonalNotesAPI {
  switch (role) {
    case 'admin':
      return adminPersonalNotesAPI;
    case 'founder':
      return founderPersonalNotesAPI;
    case 'mentor':
      return mentorPersonalNotesAPI;
  }
}

export default personalNotesAPI;
